import { Percentage } from "features/i18n/Percentage";
import {
    selectQuestionGroup,
    selectVisibleQuestionGroupIds,
    selectHasVisibleCriticalQuestions,
} from "features/qualityForm/qualityFormSlice";
import { selectFormType } from "features/qualityForm/qualityFormSlices/qualityFormCommons";

import { I18nComponent } from "react/utils/I18nComponent";
import {
    useGroupCriticalScore,
    useGroupScore,
    useWeightsEnabled,
} from "./ScorableQuestionGroup";
import { useStrings } from "features/i18n/useStrings";
import strings from "./ScoreOverview.strings";
import "./ScoreOverview.less";
import { useState } from "react";
import { GuxIcon, GuxTooltip } from "genesys-spark-components-react";
import { selectScorable } from "./scorableSlice";
import { ScoreTag } from "features/common/ScoreTag";
import { useAppSelector } from "app/hooks";
import FormType from "enums/FormType";
import { MarkdownInline } from "features/common/Markdown";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import { useLocale } from "features/i18n/useLocale";
export function QuestionGroupScore({ id }: { id: string }) {
    const name = useAppSelector(
        (state) => selectQuestionGroup(state, id)?.name
    );
    const score = useGroupScore(id, useWeightsEnabled());
    const criticalScore = useGroupCriticalScore(id, useWeightsEnabled());

    const percentage = score !== undefined ? score.total / score.max : NaN;
    const criticalPercentage =
        criticalScore !== undefined
            ? criticalScore.total / criticalScore.max
            : NaN;
    return (
        <div className="QuestionGroupScore">
            <div className="name">
                <MarkdownInline
                    markdownInlineText={name ?? ""}
                ></MarkdownInline>
            </div>
            <div className="score">
                {!isNaN(percentage) && !isNaN(criticalPercentage) && (
                    <I18nComponent
                        i18nKey="ScoreOverview.criticalPercentage"
                        substitutions={{
                            percentage: <Percentage>{percentage}</Percentage>,
                            criticalPercentage: (
                                <Percentage>{criticalPercentage}</Percentage>
                            ),
                        }}
                    />
                )}
                {isNaN(criticalPercentage) && !isNaN(percentage) && (
                    <Percentage>{percentage}</Percentage>
                )}
            </div>
        </div>
    );
}

function useFormatPercent() {
    const locale = useLocale();
    return (value: number) =>
        new Intl.NumberFormat(locale, {
            style: "percent",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
}

export function ScorableScores() {
    const t = useStrings(strings);
    const score = useAppSelector((state) => selectScorable(state)?.answers);
    const formType = useAppSelector(selectFormType);
    const formatPercent = useFormatPercent();
    const isAddScoreToQualitySummaryEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.ADD_SCORE_TO_QUALITY_SUMMARY)
    );

    const maxTotalRawCriticalScore = score?.maxTotalRawCriticalScore ?? 0;

    // Only call the selector when needed: toggle enabled AND no max score
    // If we don't need to check, return true (meaning we should show the critical score)
    const needsVisibilityCheck =
        isAddScoreToQualitySummaryEnabled && maxTotalRawCriticalScore === 0;

    // Show critical score if:
    // 1. Feature toggle is disabled, OR
    // 2. selectHasVisibleCriticalQuestions is true or maxTotalRawCriticalScore > 0
    const shouldShowCriticalScore = useAppSelector((state) =>
        needsVisibilityCheck ? selectHasVisibleCriticalQuestions(state) : true
    );

    return (
        <div className="ScorableScores">
            {score?.totalScore !== undefined && (
                <ScoreTag
                    ariaLabel={t.ariaLabels.totalScore({
                        percentage: formatPercent(score.totalScore / 100),
                    })}
                >
                    <div
                        className="tag-label-container"
                        data-testid="score-overview-total-tag"
                    >
                        <span className="tag-label">
                            {t.title.totalScoreLabel}
                        </span>
                        <Percentage>{score.totalScore / 100}</Percentage>
                    </div>
                    <GuxTooltip placement="top-start">
                        <span>{t.title.totalScoreLabel}</span>
                        <Percentage>{score.totalScore / 100}</Percentage>
                    </GuxTooltip>
                </ScoreTag>
            )}
            {score?.totalCriticalScore !== undefined &&
                shouldShowCriticalScore && (
                    <ScoreTag
                        mode="critical"
                        ariaLabel={t.ariaLabels.criticalScore({
                            percentage: formatPercent(
                                score.totalCriticalScore / 100
                            ),
                        })}
                    >
                        <div
                            className="tag-label-container"
                            data-testid="score-overview-critical-tag"
                        >
                            <span className="tag-label critical-score-label">
                                {t.title.criticalScoreLabel}
                            </span>
                            <Percentage>
                                {score.totalCriticalScore / 100}
                            </Percentage>
                        </div>
                        <GuxTooltip placement="top-start">
                            <span>{t.title.criticalScoreLabel}</span>
                            <Percentage>
                                {score.totalCriticalScore / 100}
                            </Percentage>
                        </GuxTooltip>
                    </ScoreTag>
                )}

            {formType === FormType.SURVEY && (
                <ScoreTag
                    mode="critical"
                    ariaLabel={t.ariaLabels.npsScore({
                        value:
                            score?.npsScore !== undefined
                                ? String(score.npsScore)
                                : t.title.npsScoreEmptyLabel,
                    })}
                >
                    <div
                        className="tag-label-container"
                        data-testid="score-overview-nps-tag"
                    >
                        <span className="tag-label">
                            {t.title.npsScoreLabel}
                        </span>
                        <span>
                            {score?.npsScore !== undefined
                                ? score.npsScore
                                : t.title.npsScoreEmptyLabel}
                        </span>
                    </div>
                    <GuxTooltip placement="top-start">
                        <span>
                            {t.title.npsScoreLabel}
                            {score?.npsScore !== undefined
                                ? score.npsScore
                                : t.title.npsScoreEmptyLabel}
                        </span>
                    </GuxTooltip>
                </ScoreTag>
            )}
        </div>
    );
}

export function ScoreOverview() {
    const t = useStrings(strings);
    const [showDetails, setShowDetails] = useState(true);
    const weightsEnabled = useWeightsEnabled();
    const questionGroups = useAppSelector(selectVisibleQuestionGroupIds) ?? [];
    return (
        <div
            className={`ScoreOverview gux-shadow-30 ${
                showDetails ? "expanded" : ""
            }`}
        >
            {showDetails && (
                <div className="details">
                    <div className="scores-title body-font-bold">
                        {weightsEnabled && (
                            <i className="fa fa-balance-scale" />
                        )}
                        {weightsEnabled ? t.title.scaled : t.title.unscaled}
                    </div>
                    <ol className="score-list gux-small-text">
                        {questionGroups?.map((id) => (
                            <li key={id}>
                                <QuestionGroupScore id={id} />
                            </li>
                        ))}
                    </ol>
                </div>
            )}
            <div className="summary gux-large-text">
                <div
                    role="button"
                    aria-label={`${
                        showDetails
                            ? t.statusDetailsExpanded
                            : t.statusDetailsCollapsed
                    }. ${showDetails ? t.collapseScores : t.expandScores}`}
                    aria-expanded={showDetails}
                    className="Clickable ExpandSummaryToggle"
                    data-testid="score-overview-expand-collapse-toggle"
                    onClick={() => setShowDetails(!showDetails)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            setShowDetails(!showDetails);
                        }
                    }}
                    tabIndex={0}
                >
                    <GuxIcon
                        iconName={showDetails ? "collapse" : "expand"}
                        decorative
                    ></GuxIcon>
                </div>
                <ScorableScores />
            </div>
        </div>
    );
}
